import './survey.scss';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import apiClient from '../../../../utils/apiClient';
import decodeToken from '../../../../utils/decode-token';

function flattenObject(obj, prefix = '', result = {}) {
  Object.entries(obj || {}).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, nextKey, result);
    } else {
      result[nextKey] = value;
    }
  });
  return result;
}

function getTranslationsMap(i18n) {
  const lang = i18n.resolvedLanguage || i18n.language;
  const languageData = i18n.store?.data?.[lang] || {};
  const merged = {};

  Object.values(languageData).forEach((namespaceData) => {
    const flat = flattenObject(namespaceData || {});
    Object.entries(flat).forEach(([key, value]) => {
      merged[key] = value;
    });
  });

  return merged;
}

function extractId(key) {
  const match = String(key || '').match(/-id-?(\d+)(?:-closing)?$/i);
  return match ? Number(match[1]) : null;
}

function parseQuestionKey(key, surveyId) {
  const regex = new RegExp(`^survey${surveyId}q(\\d+)[_.](\\d+)(.*)$`, 'i');
  const match = String(key || '').match(regex);

  if (!match) return null;

  const stepNumber = Number(match[1]);
  const questionNumber = Number(match[2]);
  const suffix = match[3] || '';

  if (/^-\d+$/.test(suffix)) return null;

  return {
    stepNumber,
    questionNumber,
    suffix
  };
}

function getQuestionTypeFromKey(key) {
  const normalized = String(key || '').toLowerCase();

  if (normalized.includes('text')) return 'text';
  if (normalized.includes('multi')) return 'multi';
  return 'radio';
}

function isQuestionTranslationKey(key, surveyId) {
  const normalized = String(key || '').toLowerCase();
  const questionBase = new RegExp(`^survey${surveyId}q\\d+[_.]\\d+`, 'i');

  if (!questionBase.test(key)) return false;
  if (!normalized.includes('-id')) return false;
  if (/-\d+-+id-?\d+(?:-closing)?$/i.test(key)) return false;

  return true;
}

function parseOptionTranslationKey(key, surveyId) {
  const value = String(key || '');

  const patterns = [
    new RegExp(`^survey${surveyId}(?:a|q)(\\d+)[_.](\\d+)-(\\d+)-+id-?(\\d+)$`, 'i'),
    new RegExp(`^survey${surveyId}(?:a|q)(\\d+)[_.](\\d+)-(\\d+)$`, 'i')
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) {
      return {
        stepNumber: Number(match[1]),
        questionNumber: Number(match[2]),
        optionOrder: Number(match[3]),
        optionId: match[4] ? Number(match[4]) : null
      };
    }
  }

  return null;
}

function buildInitialAnswer(question) {
  if (question.type === 'text') {
    return { text: '' };
  }

  if (question.type === 'multi') {
    return { selected: [], otherSelected: false, otherText: '', otherOptionId: null };
  }

  return { value: '', otherSelected: false, otherText: '', otherOptionId: null };
}

function isQuestionAnswered(answer, question) {
  if (!answer) return false;

  if (question.type === 'text') {
    return typeof answer.text === 'string' && answer.text.trim() !== '';
  }

  if (question.type === 'multi') {
    const selected = Array.isArray(answer.selected) ? answer.selected : [];
    const hasOtherText = typeof answer.otherText === 'string' && answer.otherText.trim() !== '';
    const hasNonOtherSelected = answer.otherOptionId
      ? selected.some((id) => id !== answer.otherOptionId)
      : selected.length > 0;

    if (!hasNonOtherSelected && !answer.otherSelected) return false;
    if (answer.otherSelected) {
      return hasOtherText || hasNonOtherSelected;
    }
    return true;
  }

  if (answer.otherSelected) {
    return typeof answer.otherText === 'string' && answer.otherText.trim() !== '';
  }

  return answer.value !== undefined && answer.value !== null && answer.value !== '';
}

function getNextStepFromAnswers(answersMap, survey) {
  if (!survey.steps.length) return 1;

  for (const step of survey.steps) {
    const questionsForThisScreen = [
      ...step.questions,
      ...(step.stepNumber === survey.steps[survey.steps.length - 1]?.stepNumber ? survey.closingQuestions : [])
    ];

    const isStepComplete = questionsForThisScreen.every((question) =>
      isQuestionAnswered(answersMap[question.key], question)
    );

    if (!isStepComplete) {
      return step.stepNumber;
    }
  }

  return survey.steps[survey.steps.length - 1].stepNumber;
}

function buildSurveyStructure(translations, surveyId) {
  const title = translations[`survey${surveyId}-title`] || '';
  const closingText = translations[`survey${surveyId}-closing`] || '';

  const stepTitles = {};
  const questionsMap = {};
  const closingQuestions = [];
  const optionsByQuestionKey = {};

  Object.entries(translations).forEach(([key, value]) => {
    const sectionMatch = key.match(new RegExp(`^survey${surveyId}-section(\\d+)-title$`, 'i'));
    if (sectionMatch) {
      stepTitles[Number(sectionMatch[1])] = value;
      return;
    }

    const optionMeta = parseOptionTranslationKey(key, surveyId);
    if (optionMeta) {
      const questionKey = `q${optionMeta.stepNumber}_${optionMeta.questionNumber}`;
      if (!optionsByQuestionKey[questionKey]) {
        optionsByQuestionKey[questionKey] = [];
      }

      const isOtherByLabel = typeof value === 'string' && value.toLowerCase().includes('other');

      optionsByQuestionKey[questionKey].push({
        key: `${questionKey}-option-${optionMeta.optionOrder}`,
        order: optionMeta.optionOrder,
        optionId: optionMeta.optionId ?? optionMeta.optionOrder,
        label: value,
        isOther: isOtherByLabel,
        sourceKey: key
      });
      return;
    }

    if (!isQuestionTranslationKey(key, surveyId)) return;

    const parsed = parseQuestionKey(key, surveyId);
    if (!parsed) return;

    const normalizedKey = key.toLowerCase();
    const question = {
      key: `q${parsed.stepNumber}_${parsed.questionNumber}`,
      stepNumber: parsed.stepNumber,
      questionNumber: parsed.questionNumber,
      questionId: extractId(key),
      sourceKey: key,
      label: value,
      type: getQuestionTypeFromKey(key),
      isBoolean: normalizedKey.includes('boolean'),
      hasOther: normalizedKey.includes('other'),
      isClosing: normalizedKey.includes('closing'),
      options: []
    };

    if (question.isClosing) {
      closingQuestions.push(question);
    } else {
      questionsMap[question.key] = question;
    }
  });

  [...Object.values(questionsMap), ...closingQuestions].forEach((question) => {
    if (question.type === 'text') return;

    const options = (optionsByQuestionKey[question.key] || [])
      .sort((a, b) => a.order - b.order)
      .map((option) => {
        const resolvedOptionId = question.isBoolean
          ? null
          : extractId(option.sourceKey) ?? option.optionId;

        const resolvedValue = question.isBoolean
          ? option.order === 1
          : resolvedOptionId;

        return {
          ...option,
          optionId: resolvedOptionId,
          value: resolvedValue
        };
      });

    if (question.hasOther && options.length > 0 && !options.some((option) => option.isOther)) {
      question.options = options.map((option, index) => ({
        ...option,
        isOther: index === options.length - 1
      }));
    } else {
      question.options = options;
    }
  });

  const stepNumbers = Array.from(
    new Set(Object.values(questionsMap).map((question) => question.stepNumber))
  ).sort((a, b) => a - b);

  const steps = stepNumbers.map((stepNumber) => ({
    stepNumber,
    title: stepTitles[stepNumber] || '',
    questions: Object.values(questionsMap)
      .filter((question) => question.stepNumber === stepNumber)
      .sort((a, b) => a.questionNumber - b.questionNumber)
  }));

  return {
    title,
    closingText,
    closingQuestions: closingQuestions.sort((a, b) => {
      if (a.stepNumber !== b.stepNumber) return a.stepNumber - b.stepNumber;
      return a.questionNumber - b.questionNumber;
    }),
    steps
  };
}

function buildAnswersFromApi(apiAnswers, survey) {
  const normalizedApiAnswers = Array.isArray(apiAnswers)
    ? apiAnswers
    : apiAnswers
      ? [apiAnswers]
      : [];

  const allQuestions = [
    ...survey.steps.flatMap((step) => step.questions),
    ...survey.closingQuestions
  ];

  const questionsById = new Map(
    allQuestions
      .filter((question) => question.questionId !== null && question.questionId !== undefined)
      .map((question) => [question.questionId, question])
  );

  const hydratedAnswers = {};

  normalizedApiAnswers.forEach((apiAnswer) => {
    const question = questionsById.get(apiAnswer.question_id);
    if (!question) return;

    if (question.type === 'text') {
      hydratedAnswers[question.key] = {
        text: apiAnswer.value ?? apiAnswer.other_text ?? ''
      };
      return;
    }

    if (question.type === 'multi') {
      const selectedIds = Array.isArray(apiAnswer.option_ids) ? apiAnswer.option_ids : [];
      const otherOption = question.options.find((option) => option.isOther);
      const otherSelected = otherOption ? selectedIds.includes(otherOption.optionId) : false;

      hydratedAnswers[question.key] = {
        selected: selectedIds,
        otherSelected,
        otherText: apiAnswer.other_text ?? '',
        otherOptionId: otherSelected ? otherOption?.optionId ?? null : null
      };
      return;
    }

    if (question.isBoolean) {
      hydratedAnswers[question.key] = {
        value: apiAnswer.value,
        otherSelected: false,
        otherText: apiAnswer.other_text ?? '',
        otherOptionId: null
      };
      return;
    }

    const otherOption = question.options.find((option) => option.isOther);
    const selectedOptionId = apiAnswer.option_id ?? null;
    const isOtherSelected = otherOption ? selectedOptionId === otherOption.optionId : false;

    hydratedAnswers[question.key] = {
      value: isOtherSelected ? '' : selectedOptionId,
      otherSelected: isOtherSelected,
      otherText: apiAnswer.other_text ?? '',
      otherOptionId: isOtherSelected ? otherOption?.optionId ?? null : null
    };
  });

  return hydratedAnswers;
}

function buildApiAnswer(question, answer) {
  if (!question?.questionId) return null;

  const questionId = extractId(question.sourceKey) ?? question.questionId;

  if (question.type === 'text') {
    return {
      question_id: questionId,
      value: answer?.text ?? ''
    };
  }

  if (question.type === 'multi') {
    const selected = Array.isArray(answer?.selected) ? answer.selected : [];
    const otherText = answer?.otherText?.trim() ?? '';
    const otherOptionId = answer?.otherOptionId ?? null;

    const optionIds = selected.filter((id) => {
      if (id !== otherOptionId) return true;
      return Boolean(otherText);
    });

    const payload = {
      question_id: questionId,
      option_ids: optionIds
    };

    if (answer?.otherSelected && otherText) {
      payload.other_text = otherText;
    }

    return payload;
  }

  if (question.isBoolean) {
    return {
      question_id: questionId,
      value: answer?.value === true
    };
  }

  if (answer?.otherSelected) {
    return {
      question_id: questionId,
      option_id: answer?.otherOptionId ?? null,
      other_text: answer?.otherText ?? ''
    };
  }

  return {
    question_id: questionId,
    option_id: answer?.value ?? null
  };
}

function formatCurrentDateTime() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function Survey({ surveyId, submissionId, hacId: externalHacId }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const decoded = token ? decodeToken(token) : null;
  const loggedHacId = decoded?.id || decoded?.hacId || decoded?.user_id || null;
  const hacId = externalHacId || loggedHacId;

  const translations = useMemo(() => getTranslationsMap(i18n), [i18n, i18n.language, i18n.resolvedLanguage]);
  const survey = useMemo(() => {
    if (!surveyId) {
      return {
        title: '',
        closingText: '',
        closingQuestions: [],
        steps: []
      };
    }

    return buildSurveyStructure(translations, surveyId);
  }, [translations, surveyId]);

  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [submissionIdState, setSubmissionIdState] = useState(submissionId || null);
  const [submissionStatus, setSubmissionStatus] = useState('in_progress');
  const [submittedAt, setSubmittedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showClosingScreen, setShowClosingScreen] = useState(false);

  const isSubmitted = submissionStatus === 'submitted';

  useEffect(() => {
    setCurrentStep(1);
    setAnswers({});
    setSubmissionIdState(submissionId || null);
    setSubmissionStatus('in_progress');
    setSubmittedAt(null);
    setSubmitting(false);
    setShowClosingScreen(false);
  }, [surveyId, submissionId]);

  useEffect(() => {
    if (!surveyId || !hacId) return;
    if (!survey.steps.length && !survey.closingQuestions.length) return;

    const fetchAnswers = async () => {
      try {
        setLoading(true);

        const response = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/answers?hacId=${hacId}`
        );

        const responseData = response.data;
        const responseAnswers = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.answers)
            ? responseData.answers
            : responseData
              ? [responseData]
              : [];

        if (responseData?.submission_id) {
          setSubmissionIdState(responseData.submission_id);
        }

        if (responseData?.status) {
          setSubmissionStatus(responseData.status);
        }

        if (responseData?.submitted_at) {
          setSubmittedAt(responseData.submitted_at);
        }

        const hydratedAnswers = buildAnswersFromApi(responseAnswers, survey);
        setAnswers(hydratedAnswers);

        if (responseData?.status === 'submitted') {
          setCurrentStep(1);
          setShowClosingScreen(false);
        } else {
          const nextStep = getNextStepFromAnswers(hydratedAnswers, survey);
          setCurrentStep(nextStep);
          setShowClosingScreen(false);
        }
      } catch (error) {
        console.error('Error fetching survey answers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [surveyId, hacId, survey]);

  useEffect(() => {
    if (!survey.steps.length) return;
    if (currentStep > survey.steps.length) {
      setCurrentStep(survey.steps.length);
    }
  }, [survey.steps.length, currentStep]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [currentStep, showClosingScreen]);

  const currentStepData = survey.steps.find((step) => step.stepNumber === currentStep) || null;
  const isLastStep = currentStep === survey.steps.length;

  const questionsForCurrentScreen = useMemo(() => {
    if (!currentStepData) return [];

    if (isLastStep) {
      return [...currentStepData.questions, ...survey.closingQuestions];
    }

    return currentStepData.questions;
  }, [currentStepData, isLastStep, survey.closingQuestions]);

  const isCurrentStepComplete = useMemo(() => {
    if (!questionsForCurrentScreen.length) return false;
    return questionsForCurrentScreen.every((question) =>
      isQuestionAnswered(answers[question.key], question)
    );
  }, [answers, questionsForCurrentScreen]);

  const postAnswerUpdate = useCallback(async (question, nextAnswer) => {
    if (!surveyId || !hacId || !question?.questionId || isSubmitted) return;

    const apiAnswer = buildApiAnswer(question, nextAnswer);
    if (!apiAnswer) return;

    try {
      await apiClient.post(
        `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/answers?hacId=${hacId}`,
        apiAnswer
      );
    } catch (error) {
      console.error('Error saving survey answer:', error);
    }
  }, [surveyId, hacId, isSubmitted]);

  const submitSurvey = useCallback(async () => {
    if (!surveyId || !hacId || !submissionIdState || isSubmitted) return;

    const submittedAtFormatted = formatCurrentDateTime();

    try {
      setSubmitting(true);

      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/submit?hacId=${hacId}`
      );

      const responseData = response.data;

      setSubmissionIdState(responseData?.submission_id ?? submissionIdState);
      setSubmissionStatus(responseData?.status ?? 'submitted');
      setSubmittedAt(responseData?.submitted_at ?? submittedAtFormatted);
      setShowClosingScreen(true);
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      setSubmitting(false);
    }
  }, [surveyId, hacId, submissionIdState, isSubmitted]);

  const goPrevious = () => {
    if (showClosingScreen) return;
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const goNext = () => {
    if (showClosingScreen) return;
    if (!isSubmitted && !isCurrentStepComplete) return;
    setCurrentStep((prev) => Math.min(survey.steps.length, prev + 1));
  };

  const goToStep = (targetStep) => {
    if (showClosingScreen) return;
    if (targetStep < 1 || targetStep > survey.steps.length) return;
    setCurrentStep(targetStep);
  };

  const handlePrimaryAction = async () => {
    if (isSubmitted) return;
    if (!isCurrentStepComplete) return;

    if (!isLastStep) {
      goNext();
      return;
    }

    await submitSurvey();
  };

  const handleTextChange = (question, value) => {
    if (isSubmitted) return;

    const nextAnswer = {
      ...(answers[question.key] || buildInitialAnswer({ type: 'text' })),
      text: value
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleTextBlur = async (question) => {
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'text' });
    await postAnswerUpdate(question, nextAnswer);
  };

  const handleRadioChange = async (question, option) => {
    if (isSubmitted) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'radio' });

    const nextAnswer = option.isOther
      ? {
        ...previous,
        value: '',
        otherSelected: true,
        otherText: previous.otherText || '',
        otherOptionId: option.optionId
      }
      : question.isBoolean
        ? {
          ...previous,
          value: option.value,
          otherSelected: false,
          otherText: '',
          otherOptionId: null
        }
        : {
          ...previous,
          value: option.optionId,
          otherSelected: false,
          otherText: '',
          otherOptionId: null
        };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));

    await postAnswerUpdate(question, nextAnswer);
  };

  const handleRadioOtherTextChange = (question, option, value) => {
    if (isSubmitted) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'radio' });

    const nextAnswer = {
      ...previous,
      otherText: value,
      otherOptionId: option.optionId
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleRadioOtherTextBlur = async (question) => {
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'radio' });
    if (!nextAnswer.otherSelected) return;
    await postAnswerUpdate(question, nextAnswer);
  };

  const handleMultiChange = async (question, option, checked) => {
    if (isSubmitted) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'multi' });
    const selected = Array.isArray(previous.selected) ? previous.selected : [];
    const resolvedOptionId = option.optionId;

    const nextAnswer = option.isOther
      ? {
        ...previous,
        selected: checked
          ? selected.filter((item) => item !== resolvedOptionId)
          : selected.filter((item) => item !== resolvedOptionId),
        otherSelected: checked,
        otherText: checked ? previous.otherText || '' : '',
        otherOptionId: checked ? resolvedOptionId : null
      }
      : {
        ...previous,
        selected: checked
          ? [...selected.filter((item) => item !== resolvedOptionId), resolvedOptionId]
          : selected.filter((item) => item !== resolvedOptionId)
      };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));

    if (option.isOther) {
      const hasText = Boolean((previous.otherText || '').trim());

      if (!checked) {
        await postAnswerUpdate(question, nextAnswer);
      } else if (hasText) {
        const nextAnswerWithOtherSelected = {
          ...nextAnswer,
          selected: [...selected.filter((item) => item !== resolvedOptionId), resolvedOptionId]
        };
        await postAnswerUpdate(question, nextAnswerWithOtherSelected);
      }

      return;
    }

    await postAnswerUpdate(question, nextAnswer);
  };

  const handleMultiOtherTextChange = (question, option, value) => {
    if (isSubmitted) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'multi' });
    const resolvedOptionId = option.optionId;
    const selected = Array.isArray(previous.selected) ? previous.selected : [];
    const hasText = Boolean(value.trim());

    const nextSelected = previous.otherSelected && hasText
      ? [...selected.filter((item) => item !== resolvedOptionId), resolvedOptionId]
      : selected.filter((item) => item !== resolvedOptionId);

    const nextAnswer = {
      ...previous,
      selected: nextSelected,
      otherText: value,
      otherOptionId: option.optionId
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleMultiOtherTextBlur = async (question) => {
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'multi' });
    if (!nextAnswer.otherSelected) return;
    if (!nextAnswer.otherText?.trim()) return;
    await postAnswerUpdate(question, nextAnswer);
  };

  const renderQuestion = (question, stepNumber) => {
    const answer = answers[question.key] || buildInitialAnswer(question);

    if (question.type === 'text') {
      return (
        <div className="textarea required" key={`${question.key}-textarea`}>
          <p>
            <strong>
              {question.label} <span>*</span>
            </strong>
          </p>
          <div>
            <textarea
              name={question.key}
              className="txt-area"
              maxLength={100}
              value={answer.text || ''}
              onChange={(e) => handleTextChange(question, e.target.value)}
              onBlur={() => handleTextBlur(question)}
              disabled={isSubmitted}
              readOnly={isSubmitted}
            />
          </div>
        </div>
      );
    }

    if (question.type === 'multi') {
      return (
        <div className="checkbox required" key={`${question.key}-checkbox`}>
          <p>
            <strong>
              {question.label} <span>*</span>
            </strong>
          </p>
          {question.options.map((option) => {
            const checked = option.isOther
              ? answer.otherSelected && answer.otherOptionId === option.optionId
              : Array.isArray(answer.selected) && answer.selected.includes(option.optionId);

            const inputId = `step${stepNumber}-q${question.questionNumber}-${option.order}`;

            return (
              <div key={`${question.key}-${option.order}`}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      onChange={(e) => handleMultiChange(question, option, e.target.checked)}
                      value={option.optionId}
                      disabled={isSubmitted}
                    />
                  }
                  label={option.label}
                />
                {option.isOther && answer.otherSelected && answer.otherOptionId === option.optionId && (
                  <textarea
                    className="txt-area"
                    name={`${question.key}-other-${option.optionId}`}
                    maxLength={100}
                    value={answer.otherText || ''}
                    onChange={(e) => handleMultiOtherTextChange(question, option, e.target.value)}
                    onBlur={() => handleMultiOtherTextBlur(question)}
                    disabled={isSubmitted}
                    readOnly={isSubmitted}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="radio required" key={`${question.key}-radio`}>
        <p>
          <strong>
            {question.label} <span>*</span>
          </strong>
        </p>
        {question.options.map((option) => {
          const checked = question.isBoolean
            ? answer.value === option.value
            : option.isOther
              ? answer.otherSelected && answer.otherOptionId === option.optionId
              : answer.value === option.optionId;
          const inputId = `step${stepNumber}-q${question.questionNumber}-${option.order}`;

          return (
            <div key={`${question.key}-${option.order}`}>
              <FormControlLabel
                control={
                  <Radio
                    id={inputId}
                    checked={checked}
                    onChange={() => handleRadioChange(question, option)}
                    value={question.isBoolean ? String(option.value) : option.optionId}
                    name={question.key}
                    disabled={isSubmitted}
                  />
                }
                label={option.label}
              />
              {option.isOther && answer.otherSelected && answer.otherOptionId === option.optionId && (
                <textarea
                  className="txt-area"
                  name={`${question.key}-other-${option.optionId}`}
                  maxLength={100}
                  value={answer.otherText || ''}
                  onChange={(e) => handleRadioOtherTextChange(question, option, e.target.value)}
                  onBlur={() => handleRadioOtherTextBlur(question)}
                  disabled={isSubmitted}
                  readOnly={isSubmitted}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading || (!survey.steps.length && !showClosingScreen)) {
    return (
      <main className="main top questionary">
        <div className="inner-questionary">
          <div className="content">
            <div className="stepper">
              <div>
                <p>Step 0 of 0</p>
                <div className="controls">
                  <button type="button" aria-label="Previous step" disabled>
                    <span className="icon-chevron-left"></span>
                  </button>
                  <button type="button" aria-label="Next step" disabled>
                    <span className="icon-chevron-right"></span>
                  </button>
                </div>
              </div>
              <div role="presentation"></div>
            </div>
          </div>
          <div className="photo step0"></div>
        </div>
      </main>
    );
  }

  if (showClosingScreen) {
    return (
      <main className="main top questionary">
        <div className="inner-questionary">
          <div className="content">
            <div className="survey-header">
              {survey.title && <h2>{survey.title}</h2>}
            </div>

            <div className="survey-content">
              <div className="survey-closing only-text">
                {survey.closingText && <p>{survey.closingText}</p>}
                <p>
                  <button
                    className="btn fit primary"
                    onClick={() => navigate('/hac/main')}
                  >
                    {t('error-error404-action')}
                  </button>
                </p>
              </div>
            </div>
          </div>

          <div className="photo closing"></div>
        </div>
      </main>
    );
  }

  if (!currentStepData) {
    return null;
  }

  return (
    <main className="main top questionary">
      <div className="inner-questionary">
        <div className="content">
          <div className="survey-header">
            {survey.title && <h2>{survey.title}</h2>}
          </div>

          <div className="stepper">
            <div>
              <p>{`Step ${currentStep} of ${survey.steps.length}`}</p>
              <div className="controls">
                <button
                  type="button"
                  aria-label="Previous step"
                  onClick={goPrevious}
                  disabled={currentStep === 1}
                >
                  <span className="icon-chevron-left"></span>
                </button>
                <button
                  type="button"
                  aria-label="Next step"
                  onClick={goNext}
                  disabled={
                    currentStep === survey.steps.length ||
                    (!isSubmitted && !isCurrentStepComplete)
                  }
                >
                  <span className="icon-chevron-right"></span>
                </button>
              </div>
            </div>

            <div role="presentation" className="steps">
              {survey.steps.map((step) => {
                const className =
                  step.stepNumber < currentStep
                    ? 'done'
                    : step.stepNumber === currentStep
                      ? 'current'
                      : 'pending';

                return (
                  <div
                    key={`step-${step.stepNumber}`}
                    className={className}
                    onClick={() => isSubmitted && goToStep(step.stepNumber)}
                    style={{ cursor: isSubmitted ? 'pointer' : 'default' }}
                  >
                  </div>
                );
              })}
            </div>
          </div>

          {currentStepData.title && (
            <h4 className="step-title">{currentStepData.title}</h4>
          )}

          <div className="survey-content">
            <div className="questions-list">
              {currentStepData.questions.map((question) => renderQuestion(question, currentStep))}
            </div>

            {isLastStep && survey.closingQuestions.length > 0 && (
              <div className="survey-closing">
                {survey.closingQuestions.map((question) => renderQuestion(question, currentStep))}
              </div>
            )}

            {!isSubmitted && (
              <div className="actions">
                <button
                  type="button"
                  className="btn primary fit"
                  onClick={handlePrimaryAction}
                  disabled={!isCurrentStepComplete || submitting}
                >
                  {submitting ? 'Submitting...' : isLastStep ? t('common-submit') : t('common-continue')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`photo step${currentStep - 1}`}></div>
      </div>
    </main>
  );
}

export default Survey;